/**
 * Workflows — Library detail view. Mirrors KernelsPage's shape: the
 * sidebar tree (StudioMenu) lists workflows; selecting one renders here
 * as a 3-column layout — config + run history down the center (the
 * trace-listing parallel), the selected run's full trace + deliverable
 * in the right column (the trace-detail parallel).
 *
 * A workflow is the agent-spine counterpart to a kernel: a saved,
 * parameterised, goal-directed analysis whose every run is RECORDED so
 * you can replay it and compare results over time — the run history is
 * the feature. Persistence is IndexedDB, per-browser, per-tenant (see
 * workflowStore). v1 is read-only; server-side sharing + the scope-
 * mounted surface come later.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import SplitPage from "../components/SplitPage.jsx";
import TabStrip from "../components/TabStrip.jsx";
import DataTable from "../components/DataTable.jsx";
import PageHero from "../components/PageHero.jsx";
import Sheet from "../components/Sheet.jsx";
import { TurnBlock } from "../components/ContextPanel.jsx";
import { KernelBlock } from "../components/KernelPrimitives.jsx";
import { useDataTableColumns } from "../lib/columnSelection.js";
import { NOTE_CLASS } from "../lib/ui.js";
import { timeLabelFormatter, dayLabelFormatter } from "../lib/traceUtils.js";

// Section eyebrow matching the trace detail panel's labels.
const EYEBROW = "text-[11px] leading-[1.08] tracking-[0.08em] uppercase text-[color:var(--eyebrow)]";
import {
    loadWorkflows,
    saveWorkflow,
    deleteWorkflow,
    loadRuns,
    subscribeWorkflows,
} from "../lib/workflowStore.js";
import { runWorkflow } from "../lib/workflowRunner.js";

export default function WorkflowsPage({ selectedItem, onSelectItem, focusHeader }) {
    const [workflows, setWorkflows] = useState([]);
    const refresh = useCallback(() => loadWorkflows(null).then(setWorkflows), []);
    useEffect(() => { refresh(); }, [refresh]);
    useEffect(() => subscribeWorkflows(() => { refresh(); }), [refresh]);

    // Selected by command (the tab payload), mirroring kernels.
    const selected = useMemo(
        () => workflows.find((w) => w.command === selectedItem) || null,
        [workflows, selectedItem],
    );

    if (!selected) return <Fallback hasAny={workflows.length > 0} />;

    return (
        <div className="flex-1 min-h-0 min-w-0 -mx-[var(--main-inset)]">
            <WorkflowDetailSplit
                key={selected.id}
                workflow={selected}
                focusHeader={focusHeader}
                onDeleted={() => onSelectItem?.(null, null)}
            />
        </div>
    );
}

function Fallback({ hasAny }) {
    return (
        <div className="grid h-full place-items-center">
            <div className="grid gap-[10px] text-center max-w-[440px]">
                <span className={`${NOTE_CLASS} text-[10px] uppercase tracking-[0.08em]`}>Workflows</span>
                <p className="text-[13px] leading-[1.5] text-[color:var(--muted)]">
                    {hasAny
                        ? "Pick a workflow from the Library sidebar to see its config and run history."
                        : "No workflows yet. Use “+ New workflow” in the Library sidebar to create one."}
                </p>
            </div>
        </div>
    );
}

// Center = config + run history; right column = open run tabs + the
// active run's detail. Runs open as tabs (multiple at once, switchable,
// closeable) — the same TabStrip pattern the explore view uses for
// trace tabs.
function WorkflowDetailSplit({ workflow, focusHeader, onDeleted }) {
    // Operate under the workflow's OWN tenant, not whatever the current
    // session resolves to — they can diverge if the workflow was saved
    // before /v1/me resolved, which would otherwise make its runs and
    // composite keys unreachable.
    const tid = workflow.tenantId;
    const [runs, setRuns] = useState([]);
    // runWorkflow's return, kept so a just-finished run shows even if
    // putRun failed soft (IDB down) and loadRuns can't see it yet.
    const [localRuns, setLocalRuns] = useState([]);
    const [openRunIds, setOpenRunIds] = useState([]);
    const [activeRunId, setActiveRunId] = useState(null);

    const refreshRuns = useCallback(
        () => loadRuns(tid, workflow.id).then(setRuns),
        [tid, workflow.id],
    );
    useEffect(() => { refreshRuns(); }, [refreshRuns]);

    // Persisted runs merged with any in-flight results (persisted wins).
    const mergedRuns = useMemo(() => {
        const byId = new Map();
        for (const r of runs) byId.set(r.id, r);
        for (const r of localRuns) if (!byId.has(r.id)) byId.set(r.id, r);
        return [...byId.values()].sort((a, b) => (b.startedAt || 0) - (a.startedAt || 0));
    }, [runs, localRuns]);

    const openRun = useCallback((runId) => {
        setOpenRunIds((prev) => (prev.includes(runId) ? prev : [...prev, runId]));
        setActiveRunId(runId);
    }, []);
    const closeRun = useCallback((runId) => {
        setOpenRunIds((prev) => {
            const next = prev.filter((id) => id !== runId);
            // If we closed the active tab, fall back to the nearest remaining one.
            setActiveRunId((cur) => (cur === runId ? (next.length ? next[next.length - 1] : null) : cur));
            return next;
        });
    }, []);

    const onRan = useCallback((run) => {
        setLocalRuns((prev) => [run, ...prev.filter((r) => r.id !== run.id)]);
        refreshRuns().then(() => openRun(run.id));
    }, [refreshRuns, openRun]);

    const activeRun = useMemo(
        () => mergedRuns.find((r) => r.id === activeRunId) || null,
        [mergedRuns, activeRunId],
    );

    const runTabs = openRunIds.map((rid) => {
        const r = mergedRuns.find((x) => x.id === rid);
        return {
            id: rid,
            label: r ? fmtRunLabel(r) : rid.slice(0, 8),
            title: r ? `Run · ${fmtTime(r.startedAt)}` : rid,
            active: rid === activeRunId,
            dot: runDotClass(r?.status),
            onSelect: () => setActiveRunId(rid),
            onClose: () => closeRun(rid),
        };
    });

    return (
        <SplitPage
            defaultDetailRatio={0.42}
            focusHeader={focusHeader}
            resetKey={workflow.id}
            // Detail opens once a run is opened, closes (clearing all open
            // run tabs) on dismiss — same interaction as the trace tabs.
            forceCollapse={openRunIds.length === 0}
            onCloseDetail={() => { setOpenRunIds([]); setActiveRunId(null); }}
            leftContent={() => (
                <div data-runs-scroll className="flex h-full min-h-0 flex-col gap-[14px] overflow-y-auto pb-[18px]">
                    <WorkflowHero workflow={workflow} runs={mergedRuns} />
                    <WorkflowSurface workflow={workflow} runs={mergedRuns} onDeleted={onDeleted} />
                    <RunsPanel
                        workflow={workflow}
                        runs={mergedRuns}
                        openRunIds={openRunIds}
                        activeRunId={activeRunId}
                        onOpen={openRun}
                        onRan={onRan}
                    />
                </div>
            )}
            rightHeader={<TabStrip tabs={runTabs} className="pt-0" />}
            rightContent={() => (
                <div className="h-full min-h-0 overflow-y-auto py-[12px]">
                    {activeRun
                        ? <RunDetail run={activeRun} />
                        : <p className="text-[11px] text-[color:var(--muted-soft)]">Open a run from the list to see its full trace and deliverable.</p>}
                </div>
            )}
        />
    );
}

// ── center: hero ─────────────────────────────────────────────────

// The explore page's eyebrow / title / metric-row header, so a
// workflow reads like any other scoped surface: identity up top, the
// run history's vital signs as the stat row.
function WorkflowHero({ workflow, runs }) {
    const metrics = useMemo(() => {
        const dash = "–";
        const last = runs[0] || null;
        const finished = runs.filter((r) => r.status && r.status !== "running");
        const done = finished.filter((r) => r.status === "done").length;
        return [
            { label: "Runs", value: runs.length ? String(runs.length) : dash },
            {
                label: "Last run",
                value: last?.startedAt ? timeLabelFormatter.format(last.startedAt) : dash,
                note: last?.startedAt ? dayLabelFormatter.format(last.startedAt) : undefined,
            },
            { label: "Completed", value: finished.length ? `${done} of ${finished.length}` : dash },
        ];
    }, [runs]);
    return (
        <PageHero
            eyebrow="Library · Workflow"
            title={(
                <span className="inline-flex min-w-0 max-w-full items-baseline gap-[10px]">
                    <span className="font-mono">/{workflow.command}</span>
                    {workflow.title && workflow.title !== workflow.command ? (
                        <span className="truncate text-[12px] font-normal tracking-normal text-[color:var(--muted-soft)]">
                            {workflow.title}
                        </span>
                    ) : null}
                </span>
            )}
            metrics={metrics}
        />
    );
}

// ── center: activity surface (goal + run-history chart) ──────────

function WorkflowSurface({ workflow, runs, onDeleted }) {
    const [editing, setEditing] = useState(false);
    const [draftJson, setDraftJson] = useState("");
    const [saveError, setSaveError] = useState(null);

    const openEditor = useCallback(() => {
        setDraftJson(JSON.stringify(stripForEditor(workflow), null, 2));
        setSaveError(null);
        setEditing(true);
    }, [workflow]);

    const save = useCallback(async () => {
        let parsed;
        try { parsed = JSON.parse(draftJson); }
        catch (e) { setSaveError(`JSON parse error: ${e.message}`); return; }
        parsed.id = workflow.id; // update in place
        // Operate under the workflow's own tenant (not the resolved
        // current one) so the composite key stays stable — see
        // WorkflowDetailSplit.
        try { await saveWorkflow(workflow.tenantId, parsed); setEditing(false); }
        catch (e) { setSaveError(e.message || String(e)); }
    }, [draftJson, workflow.id, workflow.tenantId]);

    const handleDelete = useCallback(async () => {
        await deleteWorkflow(workflow.tenantId, workflow.id);
        onDeleted?.();
    }, [workflow.tenantId, workflow.id, onDeleted]);

    const caps = workflow.capabilities || {};

    return (
        <div className="grid gap-[12px]">
            {workflow.goal ? (
                <p className="max-w-[840px] text-[12px] leading-[1.5] text-[color:var(--muted)]">{workflow.goal}</p>
            ) : null}
            <div className="flex flex-wrap gap-[5px] text-[10px]">
                <Chip>{workflow.scopeMode === "scoped" ? "scoped" : "global"}</Chip>
                {caps.analytics !== false ? <Chip>analytics</Chip> : null}
                {caps.python !== false ? <Chip>python</Chip> : null}
                {(caps.mcpServers || []).map((s) => <Chip key={s}>mcp:{s}</Chip>)}
                {(caps.kernels || []).map((k) => <Chip key={k}>/{k}</Chip>)}
            </div>
            {/* The explore page's Activity Surface chrome: dotted sheet,
                eyebrow + title, controls in the top-right corner. The
                body is the run-history chart — or, while editing, the
                config JSON editor takes the surface over (the same
                takeover move the chat column's model picker makes). */}
            <Sheet
                eyebrow="Activity surface"
                title="Steps per run"
                diagram
                tight
                actions={(
                    <div className="flex gap-[6px]">
                        <button
                            type="button"
                            onClick={editing ? save : openEditor}
                            className="inline-flex h-[26px] items-center cursor-pointer border border-[color:var(--line)] bg-[color:var(--bg)] px-[8px] text-[10px] uppercase tracking-[0.06em] text-[color:var(--muted)] hover:text-[color:var(--ink)] hover:border-[color:var(--line-strong)]"
                        >
                            {editing ? "Save config" : "Edit config"}
                        </button>
                        {editing ? (
                            <button
                                type="button"
                                onClick={() => setEditing(false)}
                                className="inline-flex h-[26px] items-center cursor-pointer border border-[color:var(--line)] bg-[color:var(--bg)] px-[8px] text-[10px] uppercase tracking-[0.06em] text-[color:var(--muted)] hover:text-[color:var(--ink)] hover:border-[color:var(--line-strong)]"
                            >
                                Cancel
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={handleDelete}
                                className="inline-flex h-[26px] items-center cursor-pointer border border-[color:var(--line)] bg-[color:var(--bg)] px-[8px] text-[10px] uppercase tracking-[0.06em] text-[color:var(--muted)] hover:text-[color:var(--red,#c0392b)] hover:border-[color:var(--red,#c0392b)]"
                            >
                                Delete
                            </button>
                        )}
                    </div>
                )}
            >
                {saveError ? (
                    <p className="text-[11px] text-[color:var(--red,#c0392b)] font-mono break-words">{saveError}</p>
                ) : null}
                {editing ? (
                    <textarea
                        value={draftJson}
                        onChange={(e) => setDraftJson(e.target.value)}
                        spellCheck={false}
                        className="w-full min-h-[260px] border border-[color:var(--line)] bg-[color:var(--bg)] p-[10px] font-mono text-[12px] leading-[1.5] text-[color:var(--ink)] outline-none focus:border-[color:var(--line-strong)]"
                    />
                ) : (
                    <RunStepsChart runs={runs} />
                )}
            </Sheet>
        </div>
    );
}

// Bar chart over the run history: one bar per run (oldest → newest),
// height = step count, color = status. Sits on a solid plot panel
// inset in the dotted sheet — the Activity Surface's anatomy. The
// step distribution is the default lens; richer lenses (duration,
// tokens) can become tabs later, like the explore surface's.
const CHART_MAX_BARS = 60;
const CHART_H = 120;

function RunStepsChart({ runs }) {
    const ordered = useMemo(
        () => [...runs].sort((a, b) => (a.startedAt || 0) - (b.startedAt || 0)),
        [runs],
    );
    const shown = ordered.slice(-CHART_MAX_BARS);
    const stepsOf = (r) => r.stepCount ?? (r.timeline?.length || 0);
    const max = Math.max(1, ...shown.map(stepsOf));
    return (
        <div className="grid gap-[6px]">
            <div
                className="flex items-end gap-[3px] overflow-hidden border border-[color:var(--line)] bg-[color:var(--bg)] px-[8px] pt-[10px]"
                style={{ height: CHART_H }}
            >
                {shown.length === 0 ? (
                    <span className="self-center px-[2px] pb-[10px] text-[11px] text-[color:var(--muted-soft)]">
                        Run this workflow to see its step distribution.
                    </span>
                ) : shown.map((r) => {
                    const steps = stepsOf(r);
                    const color = r.status === "done" ? "var(--green)"
                        : r.status === "error" ? "var(--red,#c0392b)"
                            : "var(--muted-soft)";
                    return (
                        <div
                            key={r.id}
                            title={`${fmtTime(r.startedAt)} · ${steps} step${steps === 1 ? "" : "s"} · ${r.status}`}
                            className="min-w-[4px] max-w-[22px] flex-1"
                            style={{
                                // Zero-step runs (the failure mode worth seeing)
                                // still get a 2px stub so they don't vanish.
                                height: Math.max(2, Math.round((steps / max) * (CHART_H - 24))),
                                background: color,
                                opacity: r.status === "done" ? 0.9 : 0.7,
                            }}
                        />
                    );
                })}
            </div>
            <div className="flex flex-wrap items-center gap-x-[12px] gap-y-[2px] text-[10px] leading-[1.3] text-[color:var(--muted)]">
                <span className="inline-flex items-center gap-[5px]">
                    <span className="h-[6px] w-[6px] rounded-full" style={{ background: "var(--green)" }} /> done
                </span>
                <span className="inline-flex items-center gap-[5px]">
                    <span className="h-[6px] w-[6px] rounded-full" style={{ background: "var(--red,#c0392b)" }} /> error
                </span>
                <span className="inline-flex items-center gap-[5px]">
                    <span className="h-[6px] w-[6px] rounded-full bg-[color:var(--muted-soft)]" /> aborted
                </span>
                <span className="ml-auto text-[color:var(--muted-soft)]">
                    {ordered.length > CHART_MAX_BARS ? `last ${CHART_MAX_BARS} of ${ordered.length} runs · ` : ""}
                    max {max} step{max === 1 ? "" : "s"}
                </span>
            </div>
        </div>
    );
}

// Runs render through the SAME shared DataTable the trace + execution
// logs use — identical row chrome, padding, sticky header, and
// virtualization. Only the columns + cell renderer are run-specific.
const RUN_COLUMNS = [
    { id: "time", label: "Time", width: 64 },
    { id: "result", label: "Result", width: 200, flex: true },
    { id: "steps", label: "Steps", width: 48 },
];
const RUN_VISIBLE = ["time", "result", "steps"];

// Embedded mode: the table virtualizes against the center column's
// scroll (marked data-runs-scroll) so config + runs scroll together
// and the column header sticks — exactly how the trace log embeds.
function findRunsScrollRoot(element) {
    return element ? element.closest("[data-runs-scroll]") : null;
}

function renderRunCell(run, col) {
    switch (col.id) {
        case "time":
            return (
                <div className="grid gap-0">
                    <strong className="text-[10px] leading-[1.12] font-medium">{run.startedAt ? timeLabelFormatter.format(run.startedAt) : "—"}</strong>
                    <span className="text-[10px] leading-[1.12] text-[color:var(--muted-soft)]">{run.startedAt ? dayLabelFormatter.format(run.startedAt) : ""}</span>
                </div>
            );
        case "result":
            return <RunResultCell run={run} />;
        case "steps":
            return <span className="text-[11px] leading-[1.08] font-medium">{run.stepCount ?? (run.timeline?.length || 0)}</span>;
        default:
            return null;
    }
}

function RunsPanel({ workflow, runs, openRunIds, activeRunId, onOpen, onRan }) {
    const cols = useDataTableColumns({ columns: RUN_COLUMNS, defaultVisible: RUN_VISIBLE });
    const openSet = useMemo(() => new Set(openRunIds), [openRunIds]);

    // Run-trigger state lives with the run log: the param inputs sit
    // in the log's sticky control row (where the trace listing keeps
    // its search + sort), so launching a run and watching it land in
    // the history below is one motion.
    const [paramVals, setParamVals] = useState(() => seedParams(workflow));
    const [running, setRunning] = useState(false);
    const [live, setLive] = useState(null);
    useEffect(() => { setParamVals(seedParams(workflow)); }, [workflow]);

    const handleRun = useCallback(async () => {
        if (running) return;
        setRunning(true);
        setLive({ status: "running", steps: [] });
        try {
            const params = coerceParams(workflow, paramVals);
            const run = await runWorkflow({ workflow, params, onState: setLive });
            onRan?.(run);
        } catch (e) {
            setLive({ status: "error", error: e.message || String(e) });
        } finally {
            setRunning(false);
        }
    }, [workflow, paramVals, running, onRan]);

    const params = Array.isArray(workflow.params) ? workflow.params : [];

    // Same active / context treatments the trace log applies via
    // traceRowClassName: active run = green-soft + inset stripe; an
    // open-but-inactive run = control-bg fill (the context-row look).
    const rowClassName = useCallback(
        (run) => (run.id === activeRunId
            ? "!bg-[color:var(--green-soft)] shadow-[inset_2px_0_0_var(--green)]"
            : openSet.has(run.id) ? "bg-[color:var(--control-bg)]" : ""),
        [activeRunId, openSet],
    );
    return (
        <div className="grid min-w-0">
            {/* Section header band — the trace log's eyebrow + title
                chrome, so the run history reads as this page's log. */}
            <div className="grid gap-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] px-[11px] py-[8px]">
                <span className="text-[11px] leading-[1.1] tracking-[0.06em] uppercase text-[color:var(--eyebrow)]">Run log</span>
                <span className="text-[14px] leading-[1.06] font-medium tracking-[-0.04em] text-[color:var(--ink)]">
                    Results{runs.length ? ` · ${runs.length}` : ""}
                </span>
            </div>
            {/* Control row — the trace listing's sticky filter strip
                (same classes), holding the workflow's param inputs and
                the Run trigger instead of search + sort. */}
            <div
                className="sticky z-20 grid gap-[8px] bg-[color:var(--bg)] pt-[8px] pb-[8px] shadow-[0_1px_0_var(--line-soft)]"
                style={{ top: 0 }}
            >
                <div className="flex flex-wrap items-end gap-[10px]">
                    {params.map((p) => (
                        <label key={p.name} className="grid gap-[5px]">
                            <span className={NOTE_CLASS}>
                                {p.name}<span className="text-[color:var(--muted-soft)]"> · {p.type}</span>
                            </span>
                            <input
                                value={paramVals[p.name] ?? ""}
                                onChange={(e) => setParamVals((v) => ({ ...v, [p.name]: e.target.value }))}
                                className="min-h-[30px] w-[140px] rounded-[2px] border border-[color:var(--line)] bg-transparent px-[9px] text-[12px] leading-[1.18] text-[color:var(--ink)] outline-none focus:border-[color:var(--line-strong)]"
                            />
                        </label>
                    ))}
                    <button
                        type="button"
                        onClick={handleRun}
                        disabled={running}
                        className="inline-flex min-h-[30px] items-center cursor-pointer rounded-[2px] border border-[color:var(--line)] px-[12px] text-[11px] uppercase tracking-[0.06em] text-[color:var(--ink)] hover:border-[color:var(--line-strong)] disabled:opacity-50 disabled:cursor-wait"
                    >
                        {running ? "Running…" : "▶ Run"}
                    </button>
                    {running && live ? (
                        <span className="inline-flex min-h-[30px] items-center text-[11px] text-[color:var(--muted)]">
                            {live.status} · {Array.isArray(live.steps) ? live.steps.length : 0} step{live.steps?.length === 1 ? "" : "s"}
                            {live.steps?.length ? ` · ${live.steps[live.steps.length - 1].name}` : ""}
                        </span>
                    ) : null}
                </div>
            </div>
            {runs.length === 0 ? (
                <p className="px-[6px] py-[10px] text-[11px] text-[color:var(--muted-soft)]">No runs yet. Select Run to start one.</p>
            ) : (
                <DataTable
                    cols={cols}
                    rows={runs}
                    getRowId={(r) => r.id}
                    renderCell={renderRunCell}
                    onRowClick={(r) => onOpen(r.id)}
                    rowClassName={rowClassName}
                    findScrollContainer={findRunsScrollRoot}
                />
            )}
        </div>
    );
}

// The "Result" cell — parallels the trace log's request-body cell:
// the deliverable summary when done, a pulsing "Running…" while in
// flight (the trace "Pending" analog), and a red message on failure.
function RunResultCell({ run }) {
    if (run.status === "running") {
        return (
            <span className="inline-flex min-w-0 items-center gap-[6px] text-[11px] leading-[1.08] text-[color:var(--muted-soft)]">
                <span className="h-[6px] w-[6px] shrink-0 rounded-full bg-[color:var(--muted-soft)] animate-pulse" />
                Running…
            </span>
        );
    }
    if (run.status === "error") {
        return (
            <span className="truncate text-[11px] leading-[1.08] text-[color:var(--red,#c0392b)]" title={run.error || "Run failed"}>
                {run.error || "Failed"}
            </span>
        );
    }
    if (run.deliverable?.summary) {
        return <strong className="truncate text-[11px] leading-[1.08] font-medium">{run.deliverable.summary}</strong>;
    }
    return <span className="text-[11px] leading-[1.08] text-[color:var(--muted-soft)]">No summary</span>;
}

// ── right column: run detail (trace + deliverable) ───────────────

function RunDetail({ run }) {
    const render = run.deliverable?.kind === "render" ? run.deliverable.render : null;
    const structured = run.deliverable?.kind === "structured" ? run.deliverable.result : null;
    // The run log IS a message sequence — render it with the exact
    // trace-detail TurnBlock by shaping the agent's messages into turns.
    const turns = useMemo(() => runMessagesToTurns(run.messages), [run.messages]);

    return (
        <div className="grid gap-[10px] min-w-0">
            {/* Header bar — mirrors the trace detail's headerBar. */}
            <div className="flex flex-wrap items-center gap-x-[8px] gap-y-[4px] border-b border-[color:var(--line-soft)] pb-[6px] text-[11px] leading-[1.18] text-[color:var(--muted)]">
                <StatusDot status={run.status} />
                <span>{fmtTime(run.startedAt)}</span>
                <span className="text-[color:var(--muted-soft)]">· {run.status}</span>
            </div>
            {run.error ? (
                <p className="text-[11px] text-[color:var(--red,#c0392b)] break-words">{run.error}</p>
            ) : null}

            {/* Deliverable — sits on top, in the same card chrome the
                trace detail uses for its content turn, so it reads as
                part of the same panel. */}
            <div className="grid gap-[6px] min-w-0">
                <span className={EYEBROW}>Deliverable</span>
                {render?.spec ? (
                    <div className="h-[280px] rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] p-[8px]">
                        <KernelBlock spec={render.spec} data={render.data} onOpenScope={() => {}} />
                    </div>
                ) : structured != null ? (
                    <pre className="rounded-[2px] border border-[color:var(--line)] bg-[color:var(--control-bg)] p-[8px] text-[11px] font-mono overflow-auto max-h-[280px] text-[color:var(--ink)]">{safeJson(structured)}</pre>
                ) : (
                    <p className="text-[11px] text-[color:var(--muted-soft)]">No deliverable produced.</p>
                )}
                {run.deliverable?.summary ? (
                    <p className="text-[11px] leading-[1.4] text-[color:var(--muted)]">{run.deliverable.summary}</p>
                ) : null}
            </div>

            {/* Run log — the trace-detail turn tree, verbatim. */}
            <div className="grid gap-[6px] min-w-0">
                <span className={EYEBROW}>Run log{turns.length ? ` · ${turns.length} ${turns.length === 1 ? "turn" : "turns"}` : ""}</span>
                {turns.length === 0 ? (
                    <p className="text-[11px] text-[color:var(--muted-soft)]">No steps recorded.</p>
                ) : (
                    <div className="grid gap-[6px] min-w-0">
                        {turns.map((t, i) => <TurnBlock key={i} turn={t} />)}
                    </div>
                )}
            </div>
        </div>
    );
}

// Shape the agent's pi-agent-core messages into the trace-detail turn
// model ({label, blocks}) so TurnBlock renders them identically to a
// trace's conversation. Mapping:
//   assistant message  → ASSISTANT turn (text / thinking / tool_use)
//   toolResult message → TOOL RESULT turn (consecutive results grouped)
//   user message       → USER turn (the task prompt / nudges)
// return_result is dropped — it's a framing artifact; the deliverable
// already carries its summary.
function runMessagesToTurns(messages) {
    if (!Array.isArray(messages)) return [];
    const toolName = new Map();
    for (const m of messages) {
        if (m?.role === "assistant" && Array.isArray(m.content)) {
            for (const b of m.content) if (b?.type === "toolCall") toolName.set(b.id, b.name);
        }
    }
    const turns = [];
    let results = null;
    const flushResults = () => { if (results && results.blocks.length) turns.push(results); results = null; };
    for (const m of messages) {
        if (m?.role === "assistant" && Array.isArray(m.content)) {
            flushResults();
            const blocks = [];
            for (const b of m.content) {
                if (b.type === "text" && String(b.text || "").trim()) {
                    blocks.push({ type: "text", text: b.text });
                } else if (b.type === "thinking" && String(b.thinking ?? b.text ?? "").trim()) {
                    blocks.push({ type: "thinking", thinking: b.thinking ?? b.text });
                } else if (b.type === "toolCall" && b.name !== "return_result") {
                    blocks.push({ type: "tool_use", name: b.name, input: b.arguments ?? b.input });
                }
            }
            if (blocks.length) turns.push({ label: "ASSISTANT", blocks });
        } else if (m?.role === "toolResult") {
            if (!results) results = { label: "TOOL RESULT", blocks: [] };
            results.blocks.push({
                type: "tool_result",
                content: m.content,
                isError: m.isError,
                linkedToolName: toolName.get(m.toolCallId),
            });
        } else if (m?.role === "user") {
            flushResults();
            const text = typeof m.content === "string"
                ? m.content
                : Array.isArray(m.content)
                    ? m.content.filter((c) => c?.type === "text").map((c) => c.text).join("\n")
                    : "";
            if (text.trim()) turns.push({ label: "USER", blocks: [{ type: "text", text }] });
        }
    }
    flushResults();
    return turns;
}

// ── small bits ───────────────────────────────────────────────────

function Chip({ children }) {
    return (
        <span className="border border-[color:var(--line-soft)] px-[5px] py-[1px] text-[color:var(--muted-soft)]">{children}</span>
    );
}

function StatusDot({ status }) {
    const color = status === "done" ? "var(--green,#3a9)"
        : status === "error" ? "var(--red,#c0392b)"
            : status === "aborted" ? "var(--muted-soft)"
                : "var(--warn,#e0a23c)"; // running / pending
    return <span className="inline-block h-[6px] w-[6px] rounded-full shrink-0" style={{ background: color }} aria-hidden="true" />;
}

function stripForEditor(wf) {
    const { id: _id, tenantId: _t, key: _k, createdAt: _c, updatedAt: _u, readOnly: _r, ...rest } = wf;
    return rest;
}

function seedParams(workflow) {
    const out = {};
    for (const p of (workflow?.params || [])) out[p.name] = p.default ?? "";
    return out;
}

function coerceParams(workflow, vals) {
    const out = {};
    for (const p of (workflow?.params || [])) {
        const raw = vals[p.name];
        if (raw === "" || raw == null) { out[p.name] = p.default; continue; }
        out[p.name] = p.type === "number" ? Number(raw)
            : p.type === "boolean" ? (raw === "true" || raw === true)
                : raw;
    }
    return out;
}

function fmtTime(ts) {
    if (!ts) return "—";
    try { return new Date(ts).toLocaleString(); } catch { return String(ts); }
}

// Short label for a run tab — time-of-day distinguishes runs of the
// same workflow without overflowing the strip.
function fmtRunLabel(run) {
    const ts = run?.startedAt;
    if (!ts) return "run";
    try { return new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" }); }
    catch { return "run"; }
}

// TabStrip `dot` is a className applied to the tab's status dot.
function runDotClass(status) {
    return status === "done" ? "bg-[color:var(--green,#3a9)]"
        : status === "error" ? "bg-[color:var(--red,#c0392b)]"
            : status === "running" ? "animate-pulse bg-[color:var(--warn,#e0a23c)]"
                : "bg-[color:var(--muted-soft)]";
}

function safeJson(v) {
    try { return JSON.stringify(v, null, 2); } catch { return String(v); }
}
